import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import {
  ArrowLeft,
  Check,
  Crown,
  Zap,
  Building2,
  Loader2,
  CreditCard,
  CheckCircle2,
  Clock,
  Shield,
  TrendingUp,
  Users,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import APIKeysManager from "../components/APIKeysManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_ICONS = {
  free: Zap,
  pro: Crown,
  business: Building2,
};

// Updated plan copy focused on benefits
const PLAN_COPY = {
  free: {
    title: "Gratis",
    subtitle: "Ideal para probar cómo funciona Talent Matcher",
    cta: "Plan Gratis",
    belowCta: "Sin tarjeta · Resultados en minutos",
    features: [
      "Analiza hasta 5 CVs al mes",
      "Ranking básico de candidatos",
      "Export de datos a CSV",
    ],
  },
  pro: {
    title: "Pro – Contrata con confianza",
    subtitle: "Para reclutadores y líderes que contratan con frecuencia",
    cta: "Comenzar con Pro",
    belowCta: "Cancela cuando quieras · Sin compromisos",
    features: [
      "Analiza hasta 100 CVs automáticamente",
      "Obtén un ranking claro por % de match",
      "Recibe resúmenes listos para decidir",
      "Alertas cuando aparece el mejor candidato",
      "Soporte prioritario por email",
    ],
  },
  business: {
    title: "Business – Escala tu reclutamiento",
    subtitle: "Para equipos, consultoras y áreas de RRHH",
    cta: "Contactar Ventas",
    belowCta: "Implementación asistida incluida",
    features: [
      "Puestos y CVs ilimitados",
      "Análisis avanzado con IA",
      "Exportar resultados a CSV",
      "Acceso completo a API",
      "Soporte dedicado 24/7",
    ],
  },
};

// FAQ data
const FAQS = [
  {
    question: "¿Mis CVs son confidenciales?",
    answer: "Sí. Los datos se mantienen privados y no se usan para entrenar modelos externos. Tu información está protegida.",
  },
  {
    question: "¿Talent Matcher reemplaza al reclutador?",
    answer: "No. Acelera el filtro inicial para que tomes mejores decisiones. Tú mantienes el control total del proceso.",
  },
  {
    question: "¿Puedo cancelar en cualquier momento?",
    answer: "Sí, puedes cancelar tu suscripción cuando quieras. Mantendrás acceso hasta el final del período de facturación.",
  },
  {
    question: "¿Qué pasa con mis datos si bajo de plan?",
    answer: "Tus datos se mantienen seguros. Sin embargo, no podrás crear más trabajos ni subir CVs más allá del límite de tu nuevo plan.",
  },
  {
    question: "¿Aceptan otras formas de pago?",
    answer: "PayPal acepta tarjetas de crédito/débito y cuentas bancarias en la mayoría de países.",
  },
];

export default function Billing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loginWithEmail, requireEmail } = useUser();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const checkPaymentStatus = useCallback(async () => {
    const paymentId = searchParams.get("paymentId");
    const payerId = searchParams.get("PayerID");
    
    if (paymentId && payerId) {
      try {
        const response = await axios.post(
          `${API}/paypal/capture-order?payment_id=${paymentId}&payer_id=${payerId}`
        );
        if (response.data.status === "success") {
          toast.success("¡Pago exitoso! Tu plan ha sido actualizado.");
          if (user?.email) {
            await loginWithEmail(user.email);
          }
        }
      } catch (error) {
        console.error("Payment capture error:", error);
        toast.error("Error al procesar el pago");
      }
      navigate("/billing", { replace: true });
    }
  }, [searchParams, user?.email, loginWithEmail, navigate]);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  const fetchData = async () => {
    try {
      const [plansRes, clientIdRes] = await Promise.all([
        axios.get(`${API}/plans`),
        axios.get(`${API}/paypal-client-id`),
      ]);
      setPlans(plansRes.data);
      setPaypalClientId(clientIdRes.data.client_id);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los planes");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId) => {
    if (planId === "free" || planId === currentPlan) return;
    
    requireEmail(() => {
      setSelectedPlan(planId);
      setShowPayPal(true);
    });
  };

  const getPrice = (plan) => {
    if (billingPeriod === "yearly") {
      return plan.price_yearly || plan.price * 12 * 0.8;
    }
    return plan.price;
  };

  const createOrder = async () => {
    if (!selectedPlan || !user?.email) return;
    
    const plan = plans[selectedPlan];
    const amount = getPrice(plan);
    
    try {
      const response = await axios.post(
        `${API}/paypal/create-order`,
        {
          plan_id: selectedPlan,
          amount: amount,
          billing_period: billingPeriod,
          return_url: `${window.location.origin}/billing`,
          cancel_url: `${window.location.origin}/billing`,
        },
        {
          headers: { "X-User-Email": user.email },
        }
      );
      
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
      
      return response.data.order_id;
    } catch (error) {
      console.error("Create order error:", error);
      toast.error(error.response?.data?.detail || "Error al crear la orden");
      throw error;
    }
  };

  const currentPlan = user?.plan || "free";
  const planDetails = user?.plan_details || plans[currentPlan] || {};
  
  // Calculate usage percentage for progress bar
  const maxCvs = planDetails.max_cvs_per_month || plans[currentPlan]?.cv_limit || 5;
  const usedCvs = user?.cvs_this_month || 0;
  const usagePercentage = maxCvs > 0 ? Math.min((usedCvs / maxCvs) * 100, 100) : 0;
  const isHighUsage = usagePercentage >= 70;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Cargando planes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Planes y Precios
          </h1>
          <p className="text-muted-foreground mt-1">
            Encuentra el talento ideal en minutos, no en horas
          </p>
        </div>
      </div>

      {/* Value Proposition Banner */}
      <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20">
        <CardContent className="py-6">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
              Analizar decenas de CVs manualmente puede tomar horas.
            </h2>
            <p className="text-lg text-muted-foreground">
              <span className="text-primary font-semibold">Talent Matcher lo hace en minutos.</span>
            </p>
          </div>
          
          {/* Benefits icons */}
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4 text-primary" />
              <span>Ahorra tiempo</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span>Decisiones claras</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4 text-primary" />
              <span>Menor riesgo al contratar</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Plan Banner with Usage */}
      <Card className="border-primary/20 bg-card">
        <CardContent className="py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <p className="font-medium">Plan Actual</p>
                <p className="text-sm text-muted-foreground">
                  {PLAN_COPY[currentPlan]?.title || planDetails.name || "Free"}
                </p>
              </div>
            </div>
            <Badge className="w-fit badge-strong">{currentPlan.toUpperCase()}</Badge>
          </div>
          
          {/* Usage Stats with Progress Bar */}
          <div className="mt-5 pt-5 border-t">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Uso Este Mes</p>
              <p className="text-sm text-muted-foreground">
                {usedCvs} / {maxCvs === -1 ? "∞" : maxCvs} CVs analizados
              </p>
            </div>
            
            {maxCvs !== -1 && (
              <>
                <Progress 
                  value={usagePercentage} 
                  className={`h-2 ${isHighUsage ? "[&>div]:bg-amber-500" : ""}`} 
                />
                
                {isHighUsage && (
                  <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    Estás usando la mayor parte de tu plan
                  </p>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Billing Period Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 bg-muted p-1 rounded-full">
          <button
            onClick={() => setBillingPeriod("monthly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              billingPeriod === "monthly"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Mensual
          </button>
          <button
            onClick={() => setBillingPeriod("yearly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              billingPeriod === "yearly"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Anual
            <span className="ml-1 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">
              -20%
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {Object.entries(plans).map(([planId, plan]) => {
          const Icon = PLAN_ICONS[planId] || Zap;
          const isCurrentPlan = currentPlan === planId;
          const isPopular = planId === "pro";
          const copy = PLAN_COPY[planId] || {};
          const displayPrice = billingPeriod === "yearly" 
            ? (plan.price_yearly || plan.price * 12 * 0.8)
            : plan.price;
          const monthlyEquivalent = billingPeriod === "yearly" 
            ? (displayPrice / 12).toFixed(0)
            : null;

          return (
            <Card
              key={planId}
              className={`relative flex flex-col card-hover ${
                isPopular ? "border-primary border-2 shadow-lg scale-[1.02]" : ""
              } ${isCurrentPlan ? "ring-2 ring-primary/50" : ""}`}
              data-testid={`plan-${planId}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground shadow-md">
                    ⭐ Más Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2 pt-6">
                <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${
                  isPopular ? "bg-primary" : "bg-secondary"
                }`}>
                  <Icon className={`w-6 h-6 ${isPopular ? "text-primary-foreground" : "text-foreground"}`} />
                </div>
                
                <CardTitle className="font-heading text-lg md:text-xl">
                  {copy.title || plan.name}
                </CardTitle>
                
                {copy.subtitle && (
                  <CardDescription className="text-xs mt-1">
                    {copy.subtitle}
                  </CardDescription>
                )}
                
                <div className="mt-4">
                  <span className="text-4xl font-heading font-bold">${displayPrice.toFixed(0)}</span>
                  {plan.price > 0 && (
                    <span className="text-muted-foreground text-sm">
                      /{billingPeriod === "yearly" ? "año" : "mes"}
                    </span>
                  )}
                </div>
                {monthlyEquivalent && plan.price > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    (${monthlyEquivalent}/mes)
                  </p>
                )}
              </CardHeader>
              
              <CardContent className="pt-4 flex-1 flex flex-col">
                <ul className="space-y-3 mb-6 flex-1">
                  {(copy.features || plan.features).map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="space-y-2">
                  <Button
                    className={`w-full rounded-full ${
                      isPopular 
                        ? "bg-primary hover:bg-primary/90" 
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                    }`}
                    variant={isPopular ? "default" : "secondary"}
                    disabled={isCurrentPlan || planId === "free"}
                    onClick={() => handleSelectPlan(planId)}
                    data-testid={`select-plan-${planId}`}
                  >
                    {isCurrentPlan ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Plan Actual
                      </>
                    ) : (
                      copy.cta || "Seleccionar"
                    )}
                  </Button>
                  
                  {copy.belowCta && !isCurrentPlan && (
                    <p className="text-xs text-center text-muted-foreground">
                      {copy.belowCta}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* API Keys Manager - Only shows for Business users or as upgrade prompt */}
      <APIKeysManager userPlan={currentPlan} />

      {/* PayPal Dialog */}
      <Dialog open={showPayPal} onOpenChange={setShowPayPal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pagar con PayPal</DialogTitle>
            <DialogDescription>
              {selectedPlan && plans[selectedPlan] && (
                <>Mejorar a <strong>{plans[selectedPlan].name}</strong> por <strong>${getPrice(plans[selectedPlan]).toFixed(0)}/{billingPeriod === "yearly" ? "año" : "mes"}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            {paypalClientId && (
              <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD" }}>
                <PayPalButtons
                  style={{ layout: "vertical", shape: "pill" }}
                  createOrder={createOrder}
                  onApprove={async (data) => {
                    toast.info("Procesando pago...");
                  }}
                  onError={(err) => {
                    console.error("PayPal error:", err);
                    toast.error("Error en PayPal");
                  }}
                  onCancel={() => {
                    toast.info("Pago cancelado");
                    setShowPayPal(false);
                  }}
                />
              </PayPalScriptProvider>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Preguntas Frecuentes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {FAQS.map((faq, index) => (
            <div 
              key={index}
              className="border rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
              >
                <span className="font-medium text-sm md:text-base">{faq.question}</span>
                {expandedFaq === index ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
              </button>
              {expandedFaq === index && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-muted-foreground">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Trust Badges */}
      <div className="flex flex-wrap justify-center gap-6 py-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4" />
          <span>Datos seguros</span>
        </div>
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          <span>Pago seguro con PayPal</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4" />
          <span>Cancela cuando quieras</span>
        </div>
      </div>
    </div>
  );
}
